import { lazy, Suspense, useEffect } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { api, ApiError } from '@/lib/api'
import { AdminRoute } from '@/components/admin/AdminRoute'
import type { User } from '@socialmanagement/types'

const REFRESH_TOKEN_KEY = 'sm-refresh-token'

// ── Auth pages ────────────────────────────────────────────────────────────────
const LoginPage          = lazy(() => import('@/pages/auth/LoginPage'))
const CallbackPage       = lazy(() => import('@/pages/auth/CallbackPage'))
const UnauthorizedPage   = lazy(() => import('@/pages/auth/UnauthorizedPage'))

// ── App pages ─────────────────────────────────────────────────────────────────
const DashboardPage         = lazy(() => import('@/pages/dashboard/DashboardPage'))
const NotFoundPage          = lazy(() => import('@/pages/NotFoundPage'))

// Phase 2 — Workspace & Teams
const CreateWorkspacePage    = lazy(() => import('@/pages/workspace/CreateWorkspacePage'))
const WorkspaceSettingsPage  = lazy(() => import('@/pages/workspace/WorkspaceSettingsPage'))
const TeamPage               = lazy(() => import('@/pages/workspace/TeamPage'))
const InviteAcceptPage       = lazy(() => import('@/pages/workspace/InviteAcceptPage'))

// Phase 3 — Social Accounts
const AccountsPage           = lazy(() => import('@/pages/accounts/AccountsPage'))

// Phase 4 — Posts & Composer
const PostsPage              = lazy(() => import('@/pages/posts/PostsPage'))

// Phase 5 — Content Calendar
const CalendarPage           = lazy(() => import('@/pages/calendar/CalendarPage'))

// Phase 6 — Media Library
const MediaLibraryPage       = lazy(() => import('@/pages/media/MediaLibraryPage'))

// Phase 7 — Analytics
const AnalyticsPage          = lazy(() => import('@/pages/analytics/AnalyticsPage'))

// Phase 8 — AI Studio (expanded)
const AiStudioPage           = lazy(() => import('@/pages/ai/AiStudioPage'))

// Phase 9 — Automation
const AutomationPage         = lazy(() => import('@/pages/automation/AutomationPage'))

// Phase 10 — SEO Tools
const SeoToolsPage           = lazy(() => import('@/pages/seo/SeoToolsPage'))

// Phase 11 — Developer
const DeveloperPage          = lazy(() => import('@/pages/developer/DeveloperPage'))

// Phase 12 — Notification Settings
const NotificationSettingsPage = lazy(() => import('@/pages/settings/NotificationSettingsPage'))

// Phase 13 — Account Settings
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'))

// New pages — Snippets, Campaigns, Link in Bio
const SnippetsPage   = lazy(() => import('@/pages/snippets/SnippetsPage'))
const CampaignsPage  = lazy(() => import('@/pages/campaigns/CampaignsPage'))
const LinkInBioPage  = lazy(() => import('@/pages/linkinbio/LinkInBioPage'))

// Admin pages
const AdminLayout              = lazy(() => import('@/pages/admin/AdminLayout'))
const AdminDashboardPage       = lazy(() => import('@/pages/admin/AdminDashboardPage'))
const AdminUsersPage           = lazy(() => import('@/pages/admin/UsersPage'))
const AdminUserDetailPage      = lazy(() => import('@/pages/admin/UserDetailPage'))
const AdminWorkspacesPage      = lazy(() => import('@/pages/admin/WorkspacesPage'))
const AdminWorkspaceDetailPage = lazy(() => import('@/pages/admin/WorkspaceDetailPage'))
const AdminAuditLogPage        = lazy(() => import('@/pages/admin/AuditLogPage'))
const AdminImpersonationsPage  = lazy(() => import('@/pages/admin/ImpersonationsPage'))
const AdminFeatureFlagsPage    = lazy(() => import('@/pages/admin/FeatureFlagsPage'))
const AdminPostoriaPage        = lazy(() => import('@/pages/admin/PostoriaPage'))
const AdminAllowlistPage       = lazy(() => import('@/pages/admin/AllowlistPage'))
const AdminHealthPage          = lazy(() => import('@/pages/admin/HealthPage'))
const AdminSystemSettingsPage  = lazy(() => import('@/pages/admin/SystemSettingsPage'))

// ── Auth guard ────────────────────────────────────────────────────────────────
// On first load: if persisted auth but no in-memory access token → try refresh
function AuthGuard() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const accessToken = useAuthStore((s) => s.accessToken)
  const isLoading = useAuthStore((s) => s.isLoading)
  const setAuth = useAuthStore((s) => s.setAuth)
  const setAccessToken = useAuthStore((s) => s.setAccessToken)
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const setLoading = useAuthStore((s) => s.setLoading)

  useEffect(() => {
    if (isAuthenticated && !accessToken) {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
      if (!refreshToken) { clearAuth(); return }

      setLoading(true)

      api.public
        .post<{ accessToken: string; refreshToken: string }>('/api/v1/auth/refresh', { refreshToken })
        .then(async (data) => {
          localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken)
          setAccessToken(data.accessToken)
          try {
            const me = await api.get<{ user: User }>('/api/v1/auth/me')
            setAuth(me.user, data.accessToken)
          } catch {
            setLoading(false)
          }
        })
        .catch((err) => {
          if (err instanceof ApiError && err.status === 401) {
            localStorage.removeItem(REFRESH_TOKEN_KEY)
            clearAuth()
          } else {
            setLoading(false)
          }
        })
    } else if (!isAuthenticated) {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center" style={{ background: '#0a0a0f' }}>
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#7c3aed] border-t-transparent" />
      </div>
    )
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <Outlet />
}

// Guest guard — redirect to /app if already authenticated
function GuestGuard() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  if (isAuthenticated) return <Navigate to="/app" replace />
  return <Outlet />
}

function PageFallback() {
  return (
    <div className="flex h-screen items-center justify-center" style={{ background: '#0a0a0f' }}>
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#7c3aed] border-t-transparent" />
    </div>
  )
}

const S = (C: React.LazyExoticComponent<React.ComponentType>) => (
  <Suspense fallback={<PageFallback />}><C /></Suspense>
)

export const router = createBrowserRouter([
  // ── Public auth pages ──────────────────────────────────────────────────────
  {
    path: '/login',
    element: S(LoginPage),
  },
  {
    path: '/auth/callback',
    element: S(CallbackPage),
  },
  {
    path: '/auth/unauthorized',
    element: S(UnauthorizedPage),
  },

  // Legacy redirect for old /register / /forgot-password links
  { path: '/register', element: <Navigate to="/login" replace /> },
  { path: '/forgot-password', element: <Navigate to="/login" replace /> },

  // ── Invite accept (no auth required before accepting) ─────────────────────
  {
    path: '/invite/:workspaceId/:token',
    element: <GuestGuard />,
    children: [{ index: true, element: S(InviteAcceptPage) }],
  },

  // ── Protected app routes ───────────────────────────────────────────────────
  {
    path: '/app',
    element: <AuthGuard />,
    children: [
      { index: true, element: S(DashboardPage) },

      // Workspace
      { path: 'workspace/create', element: S(CreateWorkspacePage) },
      { path: 'settings',         element: S(WorkspaceSettingsPage) },
      { path: 'team',             element: S(TeamPage) },
      { path: 'invite/:workspaceId/:token', element: S(InviteAcceptPage) },

      // Social Accounts
      { path: 'accounts', element: S(AccountsPage) },

      // Posts & Composer
      { path: 'posts',    element: S(PostsPage) },

      // Content Calendar
      { path: 'calendar', element: S(CalendarPage) },

      // Media Library
      { path: 'media',    element: S(MediaLibraryPage) },

      // Analytics
      { path: 'analytics', element: S(AnalyticsPage) },

      // AI Studio
      { path: 'ai',        element: S(AiStudioPage) },
      { path: 'ai/:tab',   element: S(AiStudioPage) },

      // Automation
      { path: 'automation', element: S(AutomationPage) },

      // SEO Tools
      { path: 'seo',        element: S(SeoToolsPage) },

      // Snippets
      { path: 'snippets',   element: S(SnippetsPage) },

      // Campaigns
      { path: 'campaigns',  element: S(CampaignsPage) },

      // Link in Bio
      { path: 'linkinbio',  element: S(LinkInBioPage) },

      // Developer
      { path: 'developer',  element: S(DeveloperPage) },

      // Settings
      { path: 'settings/notifications', element: S(NotificationSettingsPage) },
      { path: 'account',                element: S(SettingsPage) },
    ],
  },

  // ── Admin routes ───────────────────────────────────────────────────────────
  {
    path: '/admin',
    element: <AuthGuard />,
    children: [
      {
        element: <AdminRoute />,
        children: [
          {
            element: S(AdminLayout),
            children: [
              { index: true,           element: S(AdminDashboardPage) },
              { path: 'users',         element: S(AdminUsersPage) },
              { path: 'users/:id',     element: S(AdminUserDetailPage) },
              { path: 'workspaces',    element: S(AdminWorkspacesPage) },
              { path: 'workspaces/:id',element: S(AdminWorkspaceDetailPage) },
              { path: 'audit',         element: S(AdminAuditLogPage) },
              { path: 'impersonations',element: S(AdminImpersonationsPage) },
              { path: 'flags',         element: S(AdminFeatureFlagsPage) },
              { path: 'postoria',      element: S(AdminPostoriaPage) },
              { path: 'allowlist',     element: S(AdminAllowlistPage) },
              { path: 'health',        element: S(AdminHealthPage) },
              { path: 'system',        element: S(AdminSystemSettingsPage) },
              { path: 'settings',      element: S(AdminSystemSettingsPage) },
            ],
          },
        ],
      },
    ],
  },

  // ── Root redirect & 404 ────────────────────────────────────────────────────
  { path: '/', element: <Navigate to="/app" replace /> },
  { path: '*', element: S(NotFoundPage) },
])
